export type CharacterState =
  | 'Idle'
  | 'Walk'
  | 'Run'
  | 'Jump'
  | 'Fall'
  | 'Land'
  | 'Grab';

export interface StateMachineContext {
  grounded: boolean;
  moveInput: boolean;
  running: boolean;
  jumpTriggered: boolean;
  grabTriggered: boolean;
  rising?: boolean;
}

export type StateChangeListener = (
  from: CharacterState,
  to: CharacterState,
) => void;

const TRANSITIONS: Record<CharacterState, CharacterState[]> = {
  Idle: ['Walk', 'Run', 'Jump', 'Grab'],
  Walk: ['Idle', 'Run', 'Jump', 'Grab'],
  Run: ['Idle', 'Walk', 'Jump', 'Grab'],
  Jump: ['Fall', 'Land'],
  Fall: ['Land', 'Jump'],
  Land: ['Idle', 'Walk', 'Run'],
  Grab: ['Idle', 'Walk', 'Run'],
};

export class CharacterStateMachine {
  private current: CharacterState = 'Idle';
  private listeners: StateChangeListener[] = [];

  get state(): CharacterState {
    return this.current;
  }

  onChange(listener: StateChangeListener): void {
    this.listeners.push(listener);
  }

  canTransition(to: CharacterState): boolean {
    return TRANSITIONS[this.current].includes(to);
  }

  transition(to: CharacterState): boolean {
    if (to === this.current) return false;
    if (!this.canTransition(to)) return false;
    const from = this.current;
    this.current = to;
    for (const l of this.listeners) l(from, to);
    return true;
  }

  evaluate(ctx: StateMachineContext): CharacterState {
    const s = this.current;

    if (s === 'Jump' || s === 'Fall') {
      if (ctx.jumpTriggered) return this.setTo('Jump');
      if (ctx.grounded) return this.setTo('Land');
      if (s === 'Jump' && !ctx.rising) return this.setTo('Fall');
      return s;
    }

    if (s === 'Land') {
      return s;
    }

    if (s === 'Grab') {
      if (!ctx.grabTriggered) {
        return this.setTo(ctx.moveInput ? (ctx.running ? 'Run' : 'Walk') : 'Idle');
      }
      return s;
    }

    if (ctx.grabTriggered && (s === 'Idle' || s === 'Walk' || s === 'Run')) {
      return this.setTo('Grab');
    }

    if (ctx.jumpTriggered) {
      return this.setTo('Jump');
    }

    if (!ctx.grounded) {
      return this.setTo('Fall');
    }

    if (ctx.moveInput) {
      return this.setTo(ctx.running ? 'Run' : 'Walk');
    }

    return this.setTo('Idle');
  }

  forceState(to: CharacterState): void {
    const from = this.current;
    this.current = to;
    for (const l of this.listeners) l(from, to);
  }

  private setTo(to: CharacterState): CharacterState {
    if (to === this.current) return this.current;
    const from = this.current;
    this.current = to;
    for (const l of this.listeners) l(from, to);
    return this.current;
  }
}